import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';
import { isEqual } from 'lodash';
import {
  Mutation,
  MutationUpdateContactArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactFormValues } from 'src/components/domain/contacts/contact-form/contact-form.schema';
import {
  getContactUpsertVariables,
  PossibleDuplicateContactState,
  useContactUpsertErrorHandler,
} from 'src/components/domain/contacts/contact-form/use-contact-upsert-error-handler';
import { CONTACT_FIELDS_FRAGMENT } from 'src/hooks/gql/use-refresh-contact';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const UPDATE_CONTACT_MUTATION = gql`
  ${CONTACT_FIELDS_FRAGMENT}
  mutation UpdateContact($input: UpdateContactInput!, $allowDuplicates: Boolean!) {
    updateContact(input: $input, allowDuplicates: $allowDuplicates) {
      contact {
        ...ContactFields
      }
    }
  }
`;

type UseUpdateContactAutosaveParams = {
  form: UseFormReturn<ContactFormValues>;
  contactId: number | undefined;
};

export const useUpdateContactAutosave = ({
  form,
  contactId,
}: UseUpdateContactAutosaveParams) => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const [possibleDuplicateContact, setPossibleDuplicateContact] =
    useState<PossibleDuplicateContactState | null>(null);

  const [updateContact, { loading: isUpdatingContact }] = useMutation<
    Pick<Mutation, 'updateContact'>,
    MutationUpdateContactArgs
  >(UPDATE_CONTACT_MUTATION);

  const { handleUpsertError, getConflictField } = useContactUpsertErrorHandler({
    form,
    onPossibleDuplicate: setPossibleDuplicateContact,
    markConflictField: true,
  });

  const onAutosave = useCallback(
    async (values: Partial<ContactFormValues>) => {
      const baseVariables = getContactUpsertVariables(values);

      await updateContact({
        variables: {
          allowDuplicates: false,
          input: {
            ...baseVariables.input,
            contactId: Number(contactId),
          },
        },
        onError: (error) =>
          handleUpsertError(error, {
            allowDuplicates: false,
            institutionId: currentInstitution.id,
            ...baseVariables,
          }),
        onCompleted: () => {
          (Object.keys(values) as (keyof ContactFormValues)[]).forEach((name) => {
            // If the field changed again while the mutation was in flight, don't
            // mark it clean: leave it dirty so the next autosave cycle retries it.
            if (isEqual(form.getValues(name), values[name])) {
              form.resetField(name, { defaultValue: values[name] });
            }
          });
        },
      });
    },
    [updateContact, contactId, handleUpsertError, currentInstitution.id, form],
  );

  const onAddAnyway = useCallback(async () => {
    if (!possibleDuplicateContact) {
      return;
    }

    await updateContact({
      variables: {
        input: {
          ...possibleDuplicateContact.queryVariables.input,
          contactId: Number(contactId),
        },
        allowDuplicates: true,
      },
      onCompleted: () => {
        const conflictField = getConflictField(possibleDuplicateContact.duplicateData);

        if (conflictField) {
          form.clearErrors(conflictField);
          form.reset(undefined, {
            keepValues: true,
            keepDirty: false,
          });
        }

        setPossibleDuplicateContact(null);
      },
      onError: handleGraphQLError,
    });
  }, [
    possibleDuplicateContact,
    updateContact,
    contactId,
    getConflictField,
    form,
    handleGraphQLError,
  ]);

  const closePossibleDuplicateModal = useCallback(() => {
    setPossibleDuplicateContact(null);
  }, []);

  return {
    onAutosave,
    isUpdatingContact,
    possibleDuplicateContact,
    onAddAnyway,
    closePossibleDuplicateModal,
  };
};
